import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Clock, Search, Loader2, User } from 'lucide-react';
import { getAdminReferralApplications, reviewReferralApplication } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Button from '../../components/ui/Button';

interface Application {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  reason: string;
  status: string;
  review_notes: string;
  created_at: string;
  updated_at: string;
}

export default function AdminReferralApplications(): React.JSX.Element {
  const toast = useToast();
  const [apps, setApps] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [reviewing, setReviewing] = useState<number | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<number, string>>({});

  useEffect(() => {
    loadApplications();
  }, [filter]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const res = await getAdminReferralApplications(filter === 'all' ? undefined : filter);
      setApps((res as any).applications || []);
    } catch (e: any) {
      toast.show(e.message || 'Failed to load applications', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (id: number, status: string) => {
    setReviewing(id);
    try {
      await reviewReferralApplication(id, {
        status,
        review_notes: reviewNotes[id] || '',
      });
      toast.show(`Application ${status}`, 'success');
      setReviewNotes((prev) => ({ ...prev, [id]: '' }));
      loadApplications();
    } catch (e: any) {
      toast.show(e.message || 'Failed to review application', 'error');
    } finally {
      setReviewing(null);
    }
  };

  const filtered = apps.filter(
    (a) =>
      a.user_name?.toLowerCase().includes(search.toLowerCase()) ||
      a.user_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Referral Applications</h1>
          <p className="text-sm text-slate-500 mt-1">Review and approve referral program applications</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
          />
        </div>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', 'all'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${
                filter === f
                  ? 'bg-amber-500 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-amber-500'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
            <p className="text-sm text-slate-500 mt-2">Loading...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No applications found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Applicant</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Reason</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Applied On</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Status</th>
                  <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((app) => (
                  <motion.tr key={app.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-slate-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{app.user_name || 'User'}</p>
                          <p className="text-xs text-slate-500">{app.user_email || 'No email'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-600 max-w-xs truncate" title={app.reason}>
                        {app.reason || 'No reason provided'}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        app.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                        app.status === 'rejected' ? 'bg-red-50 text-red-700' :
                        'bg-amber-50 text-amber-700'
                      }`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {app.status === 'pending' ? (
                        <div className="flex flex-col gap-2">
                          <input
                            type="text"
                            placeholder="Review notes (optional)"
                            value={reviewNotes[app.id] || ''}
                            onChange={(e) => setReviewNotes((prev) => ({ ...prev, [app.id]: e.target.value }))}
                            className="w-48 px-3 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleReview(app.id, 'approved')}
                              disabled={reviewing === app.id}
                              className="flex items-center gap-1"
                            >
                              {reviewing === app.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReview(app.id, 'rejected')}
                              disabled={reviewing === app.id}
                              className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <X className="w-3 h-3" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {app.review_notes && (
                            <p className="text-xs text-slate-500">{app.review_notes}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {app.updated_at !== app.created_at ? `Reviewed ${new Date(app.updated_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
